import React, { useEffect, useState } from 'react'
import { DateTime, Interval } from 'luxon'
import { observer } from 'mobx-react-lite'
import { Stack, Step, StepButton, Stepper } from '@mui/material'
import { ChevronLeftRounded } from '@mui/icons-material'
import { color } from '@trivie/ui-web/theme'

import { Dialog } from '@trivie/ui-web/components/dialogs/dialog'
import { Text } from '@trivie/ui-web/components/text'
import { Button } from '@trivie/ui-web/components/button'
import {
  useStores,
  PostSurveyAssignDetailsRequestType,
  GetSurveyAssignDetailsResponseType,
  ISelectorOption,
} from '@trivie/core'
import { SurveyApi } from '@trivie/core/src/services/api-objects'
import { useHistory } from 'react-router'
import { SurveyAssignees } from './steps/survey-assignees'
import { SurveyDetails } from './steps/survey-details'

export interface IFormInputs {
  title: string
  start_date: string
  start_time: string
  end_date: string
  end_time: string
  start_time_zone: string
  end_time_zone: string
  is_anonymous: boolean
  notify: boolean
  open_ended: boolean
}

export interface IAssignSurveyDialog {
  requestClose: () => void
  open: boolean
}

export const AssignSurveyDialog = observer((props: IAssignSurveyDialog) => {
  const { open, requestClose } = props
  const { surveyStore } = useStores()
  const {
    surveyErrorMessage,
    setSurveyErrorMessage,
    selectedSurveyId,
    getSurveyAssignDetails,
    postSurveyAssignDetails,
  } = surveyStore
  const history = useHistory()

  const [activeStep, setActiveStep] = useState(0)
  const [selectedOptions, setSelectedOptions] = useState<ISelectorOption[]>([])
  const [userCount, setUserCount] = useState(0)
  const [preselectIds, setPreselectIds] = useState<{ id: number; type: string }[] | undefined>()

  const [fd, setFd] = useState<IFormInputs>({
    title: '',
    start_date: DateTime.local().toFormat('yyyy-MM-dd'),
    start_time: DateTime.now().toLocaleString(DateTime.TIME_24_SIMPLE),
    end_date: DateTime.local().plus({ days: 30 }).toFormat('yyyy-MM-dd'),
    end_time: '23:59',
    start_time_zone: '',
    end_time_zone: '',
    is_anonymous: false,
    notify: false,
    open_ended: false,
  })

  const [touched, setTouched] = useState<Record<string, boolean>>({
    title: false,
    start_date: false,
    start_time: false,
    end_date: false,
    end_time: false,
  })

  const [surveyList, setSurveyList] = useState<
    Array<{
      survey_id: number
      survey_title: string
      survey_start_date: string
      survey_end_date: string
    }>
  >([])

  const steps = ['Assignees', 'Details']

  // Load survey data on mount
  useEffect(() => {
    if (!open) return

    setActiveStep(0)
    setSelectedOptions([])
    setUserCount(0)
    setPreselectIds(undefined)

    // Fetch live/scheduled surveys for duplicate checking
    Promise.all([
      SurveyApi.getAllSurveys(
        'surveys',
        false,
        [
          { field: 'survey_id' },
          { field: 'survey_title' },
          { field: 'survey_start_date' },
          { field: 'survey_end_date' },
        ],
        'list_live_surveys',
        undefined,
        'live',
      ),
      SurveyApi.getAllSurveys(
        'surveys',
        false,
        [
          { field: 'survey_id' },
          { field: 'survey_title' },
          { field: 'survey_start_date' },
          { field: 'survey_end_date' },
        ],
        'list_scheduled_surveys',
        undefined,
        'scheduled',
      ),
    ]).then((data) => {
      const surveys = [...data[0].data.rows, ...data[1].data.rows]
      setSurveyList(
        surveys.map((s: any) => ({
          survey_id: s.columns.find((c: any) => c.field === 'survey_id').value,
          survey_title: s.columns.find((c: any) => c.field === 'survey_title').value,
          survey_start_date: s.columns.find((c: any) => c.field === 'survey_start_date').value,
          survey_end_date: s.columns.find((c: any) => c.field === 'survey_end_date').value,
        })),
      )
    })

    // Fetch survey details for form defaults
    getSurveyAssignDetails(selectedSurveyId!).then((resp: GetSurveyAssignDetailsResponseType) => {
      if (!resp) return

      setFd({
        title: resp.title,
        start_date: DateTime.fromISO(resp.start_datetime || DateTime.now().toString()).toFormat(
          'yyyy-MM-dd',
        ),
        start_time: DateTime.fromISO(resp.start_datetime || DateTime.now().toString()).toFormat(
          'HH:mm',
        ),
        start_time_zone: '',
        end_date: DateTime.fromISO(
          resp.end_datetime || DateTime.now().plus({ days: 30 }).toString(),
        ).toFormat('yyyy-MM-dd'),
        end_time: DateTime.fromISO(resp.end_datetime || DateTime.now().toString()).toFormat(
          'HH:mm',
        ),
        end_time_zone: '',
        is_anonymous: resp.is_anonymous,
        notify: resp.notify,
        open_ended: !!resp.start_datetime && !resp.end_datetime,
      })

      // Build preselectIds from existing assignees
      // const ids: { id: number; type: string }[] = []
      // resp.users?.forEach((u: any) => ids.push({ id: u.user_id, type: 'Users' }))
      // resp.groups?.forEach((g: any) => ids.push({ id: g.group_id, type: 'Groups' }))
      // if (ids.length > 0) setPreselectIds(ids)
    })
  }, [open])

  // Validate duplicate overlap when survey list or form data changes
  useEffect(() => {
    if (surveyList.length > 0 && fd.title) {
      const hasDuplicate = checkForDuplicateOverlap(
        fd.title,
        fd.start_date,
        fd.start_time,
        fd.end_date,
        fd.end_time,
      )
      if (hasDuplicate) {
        setTouched((prev) => ({ ...prev, title: true }))
      }
    }
  }, [surveyList, fd.title, fd.start_date, fd.start_time, fd.end_date, fd.end_time])

  const checkForDuplicateOverlap = (
    title: string,
    startDate: string,
    startTime: string,
    endDate: string,
    endTime: string,
  ): boolean => {
    if (!title || !startDate || !startTime || !endDate || !endTime) return false

    const formStart = DateTime.fromFormat(`${startDate} ${startTime}`, 'yyyy-MM-dd HH:mm')
    const formEnd = DateTime.fromFormat(`${endDate} ${endTime}`, 'yyyy-MM-dd HH:mm')
    if (!formStart.isValid || !formEnd.isValid) return false

    const formInterval = Interval.fromDateTimes(formStart, formEnd)

    return surveyList.some((survey) => {
      if (title.trim() !== survey.survey_title.trim()) return false
      if (survey.survey_id === selectedSurveyId) return false

      const surveyStart = DateTime.fromISO(survey.survey_start_date)
      const surveyEnd = DateTime.fromISO(survey.survey_end_date)
      if (!surveyStart.isValid || !surveyEnd.isValid) return false

      const surveyInterval = Interval.fromDateTimes(surveyStart, surveyEnd)
      return formInterval.overlaps(surveyInterval) || formInterval.equals(surveyInterval)
    })
  }

  const validate = () => {
    const errors: Record<string, { type: string; message: string }> = {}
    if (!fd.title) {
      errors.title = { type: 'required', message: 'Name is required' }
    }
    if (Object.keys(errors).length) return errors

    if (
      !fd.open_ended &&
      checkForDuplicateOverlap(fd.title, fd.start_date, fd.start_time, fd.end_date, fd.end_time)
    ) {
      errors.title = {
        type: 'duplicate',
        message: 'A survey with this name already exists during this time period',
      }
    }
    return errors
  }

  const errors = validate()

  const handleAddUserGroup = (selected: ISelectorOption[]) => {
    setSelectedOptions(selected)
    setUserCount(selected.reduce((acc, s) => (s?.number_users ? acc + s.number_users : acc + 1), 0))
  }

  const onSubmit = () => {
    const data: PostSurveyAssignDetailsRequestType = {
      id: selectedSurveyId!,
      title: fd.title,
      is_anonymous: fd.is_anonymous,
      start_datetime: DateTime.fromFormat(`${fd.start_date} ${fd.start_time}`, 'yyyy-MM-dd hh:mm')
        .toUTC()
        .toISO(),
      end_datetime: fd.open_ended
        ? null
        : DateTime.fromFormat(`${fd.end_date} ${fd.end_time}`, 'yyyy-MM-dd hh:mm').toUTC().toISO(),
      groups: selectedOptions.filter((v) => v.type === 'Groups').map((m) => m.id) as any,
      users: selectedOptions.filter((v) => v.type === 'Users').map((m) => m.id) as any,
      notify: !!fd.notify,
    }

    postSurveyAssignDetails(selectedSurveyId!, data).then((resp: any) => {
      if (resp.kind !== 'conflict') {
        setActiveStep(3)
      }
    })
  }

  const stepIsValid = () => {
    if (activeStep === 0) return selectedOptions.length > 0
    if (activeStep === 1) return Object.keys(errors).length === 0
    return true
  }

  const handleNext = () => {
    if (activeStep === 2) {
      onSubmit()
    } else if (activeStep === 3) {
      handleClose()
    } else {
      setActiveStep(activeStep + 1)
    }
  }

  const handleBack = () => setActiveStep((prev) => prev - 1)

  const handleClose = () => {
    setActiveStep(0)
    requestClose()
    if (window.location.pathname.includes('/create')) {
      history.push('/activities/surveys/live')
    }
  }

  const getButtonText = () => {
    if (activeStep === 1) return 'Assign'
    if (activeStep === 2) return 'Confirm'
    if (activeStep === 3) return 'Close'
    return 'Next'
  }

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <SurveyAssignees
            onSelectionChange={handleAddUserGroup}
            defaultOptions={selectedOptions.length > 0 ? selectedOptions : undefined}
          />
        )
      case 1:
        return (
          <SurveyDetails
            fd={fd}
            setFd={setFd}
            touched={touched}
            setTouched={setTouched}
            errors={errors}
          />
        )
      case 2:
        return (
          <div style={CONFIRM}>
            <Text
              style={{ color: color.shade70 }}
              variant="body1"
              text={`Are you sure you want to assign this survey?`}
            />
          </div>
        )
      case 3:
        return (
          <div style={CONFIRM}>
            <Text style={{ color: color.shade70 }} variant="body1" text="Survey assigned!" />
          </div>
        )
      default:
        return null
    }
  }

  return (
    <>
      <Dialog
        onClose={handleClose}
        title="Assign Survey"
        open={open}
        maxWidth={activeStep < 2 ? 'md' : 'xs'}
        disableBackdropClick
        sx={{
          '& .MuiDialogContent-root': {
            display: 'flex',
            flexDirection: 'column',
            height: activeStep === 0 ? 600 : 'auto',
          },
        }}
        actions={
          <Stack
            flex={1}
            direction="row"
            useFlexGap
            gap="12px"
            justifyContent={activeStep === 0 || activeStep === 3 ? 'flex-end' : 'space-between'}
          >
            {activeStep > 0 && activeStep < 3 && (
              <Button
                onClick={handleBack}
                startIcon={<ChevronLeftRounded />}
                variant="text"
                text="Back"
              />
            )}
            <Button onClick={handleNext} disabled={!stepIsValid()} text={getButtonText()} />
          </Stack>
        }
      >
        {activeStep < 2 && (
          <Stepper
            style={{ paddingLeft: 0, paddingRight: 0, marginBottom: 32 }}
            nonLinear
            activeStep={activeStep}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepButton disableRipple disabled>
                  {label}
                </StepButton>
              </Step>
            ))}
          </Stepper>
        )}
        <div
          style={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            overflowY: 'hidden',
            height: activeStep < 2 ? 500 : 'auto',
          }}
        >
          {getStepContent(activeStep)}
        </div>
      </Dialog>
      <Dialog
        danger
        open={!!surveyErrorMessage}
        title="Duplicate Survey"
        dialogText={surveyErrorMessage || ''}
        confirmBtnLabel="Okay"
        maxWidth="xs"
        onClose={() => setSurveyErrorMessage('')}
        onConfirm={() => setSurveyErrorMessage('')}
      />
    </>
  )
})

const CONFIRM: React.CSSProperties = {
  display: 'flex',
  flex: 1,
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
}
